import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Menu } from "lucide-react";
import StarField from "./StarField";
import Aurora from "./Aurora";
import MobileMenu from "./ms/MobileMenu";
import SafeFx from "./ms/SafeFx";
import NavDropdown from "./ms/NavDropdown";
import { useInvite } from "./ms/InviteContext";
import { NAV_LINKS } from "../lib/site";

const leftLinks = NAV_LINKS.slice(0, 3);
const rightLinks = NAV_LINKS.slice(3);

const MOON_RADIUS = 12;
const HALO_RADIUS = 11;
const BITE_RADIUS = 32;
const PILL_HEIGHT = 52;
const BAR_H = 36;
const NAV_TOP = BAR_H + 26;

/* Liquid Glass pill */
const liquidGlassStyle = {
  height: PILL_HEIGHT,
  width: 540,
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 45%, rgba(255,255,255,0.02) 100%)",
  backdropFilter: "blur(22px) saturate(180%)",
  WebkitBackdropFilter: "blur(22px) saturate(180%)",
  boxShadow: [
    "inset 0 1px 0 rgba(255,255,255,0.35)",
    "inset 0 0 0 1px rgba(255,255,255,0.06)",
    "inset 0 -8px 18px -10px rgba(255,255,255,0.06)",
    "inset 0 -1px 0 rgba(0,0,0,0.25)",
    "0 18px 40px -14px rgba(0,0,0,0.55)",
    "0 2px 6px rgba(0,0,0,0.35)",
  ].join(", "),
};

const Pill = ({ links, side }) => {
  const isLeft = side === "left";
  const mask = isLeft
    ? `radial-gradient(circle ${BITE_RADIUS}px at 100% 50%, transparent 99%, black 100%)`
    : `radial-gradient(circle ${BITE_RADIUS}px at 0% 50%, transparent 99%, black 100%)`;
  return (
    <div
      className={`pointer-events-auto relative flex items-center justify-center ${
        isLeft ? "rounded-l-full pl-7 pr-16" : "rounded-r-full pr-7 pl-16"
      }`}
      style={{ ...liquidGlassStyle, WebkitMaskImage: mask, maskImage: mask }}
      data-testid={`nav-pill-${side}`}
    >
      {links.map((l) => {
        const className = "relative px-2.5 text-[11px] tracking-[0.2em] uppercase text-white/75 hover:text-white transition-colors duration-300 whitespace-nowrap";
        const testId = `nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`;
        if (l.children) return <NavDropdown key={l.label} item={l} className={className} prefix="nav-link" />;
        return (
          <Link key={l.label} to={l.href} data-testid={testId} className={className}>
            {l.label}
          </Link>
        );
      })}
    </div>
  );
};

const MoonDisc = () => (
  <div
    aria-hidden
    data-testid="moon-disc"
    className="relative z-10 rounded-full pointer-events-none shrink-0"
    style={{
      width: MOON_RADIUS * 2,
      height: MOON_RADIUS * 2,
      background:
        "radial-gradient(circle at 36% 32%, #fbf5dd 0%, #f0e7c9 50%, #d6cdac 86%, #b3a98a 100%)",
      boxShadow:
        "inset -1.6px -1.6px 2px rgba(40,30,15,0.32), 0 0 1px rgba(245,240,222,0.45)",
    }}
  />
);

/* ---------- Photoreal pine silhouettes (PNGs in /public/trees/) ----------
   We use CSS mask-image so the alpha shape of each PNG is preserved while
   we recolor it to a deep navy silhouette that blends with the sky. */

// [src, naturalWidth, naturalHeight] for individual tree PNGs (tall single trees)
const TALL_TREES = [
  ["/trees/tree_new.webp", 1549, 2000],
  ["/trees/tree_new.webp", 1549, 2000],
  ["/trees/tree1.webp", 775, 1280],
  ["/trees/tree_new.webp", 1549, 2000],
  ["/trees/tree4.webp", 810, 1280],
  ["/trees/tree_new.webp", 1549, 2000],
  ["/trees/tree5.webp", 990, 1280],
  ["/trees/tree7.webp", 821, 1280],
  ["/trees/tree8.webp", 668, 1280],
  ["/trees/tree2.webp", 1280, 1280],
];

// Wide multi-tree forest panoramas — use as continuous strips
const PANORAMAS = [
  ["/trees/tree3.webp", 1280, 907],
  ["/trees/tree6.webp", 1280, 677],
];

const Tree = ({ src, naturalW, naturalH, heightVh, flip, color }) => (
  <div
    aria-hidden
    style={{
      height: `${heightVh}vh`,
      width: `${heightVh * (naturalW / naturalH)}vh`,
      backgroundColor: color,
      WebkitMaskImage: `url(${src})`,
      maskImage: `url(${src})`,
      WebkitMaskRepeat: "no-repeat",
      maskRepeat: "no-repeat",
      WebkitMaskPosition: "bottom center",
      maskPosition: "bottom center",
      WebkitMaskSize: "contain",
      maskSize: "contain",
      transform: flip ? "scaleX(-1)" : undefined,
    }}
  />
);

const Panorama = ({ src, naturalW, naturalH, heightVh, color, flip }) => (
  <div
    aria-hidden
    className="absolute left-0 right-0 bottom-0"
    style={{
      height: `${heightVh}vh`,
      backgroundColor: color,
      WebkitMaskImage: `url(${src})`,
      maskImage: `url(${src})`,
      WebkitMaskRepeat: "repeat-x",
      maskRepeat: "repeat-x",
      WebkitMaskPosition: "bottom left",
      maskPosition: "bottom left",
      WebkitMaskSize: `auto ${heightVh}vh`,
      maskSize: `auto ${heightVh}vh`,
      transform: flip ? "scaleX(-1)" : undefined,
    }}
  />
);

const ForestLayer = ({ trees, z, blur, bottomOffsetVh, opacity, color }) => (
  <div
    aria-hidden
    className="absolute left-0 right-0 pointer-events-none select-none"
    style={{
      bottom: `${bottomOffsetVh}vh`,
      zIndex: z,
      opacity,
      filter: blur ? `blur(${blur}px)` : "none",
    }}
  >
    {trees.map((t, i) => {
      const [src, w, h] = TALL_TREES[t.kind % TALL_TREES.length];
      return (
        <div
          key={i}
          className="absolute"
          style={{
            left: `${t.left}%`,
            bottom: 0,
            transform: "translateX(-50%)",
          }}
        >
          <Tree
            src={src}
            naturalW={w}
            naturalH={h}
            heightVh={t.heightVh}
            flip={t.flip}
            color={color}
          />
        </div>
      );
    })}
  </div>
);

/* ---------- (Removed) Volumetric light beam — replaced by perspective trees ---------- */


const Forest = () => {
  // Distant tree line — smaller, atmospheric haze (faded, blurred)
  const distant = [
    { left: 2, heightVh: 14, kind: 0, flip: false },
    { left: 8, heightVh: 12, kind: 1, flip: true },
    { left: 14, heightVh: 16, kind: 2, flip: false },
    { left: 20, heightVh: 13, kind: 3, flip: true },
    { left: 26, heightVh: 15, kind: 4, flip: false },
    { left: 32, heightVh: 12, kind: 0, flip: true },
    { left: 38, heightVh: 16, kind: 5, flip: false },
    { left: 44, heightVh: 13, kind: 1, flip: true },
    { left: 50, heightVh: 14, kind: 2, flip: false },
    { left: 56, heightVh: 12, kind: 3, flip: true },
    { left: 62, heightVh: 15, kind: 4, flip: false },
    { left: 68, heightVh: 13, kind: 0, flip: true },
    { left: 74, heightVh: 16, kind: 5, flip: false },
    { left: 80, heightVh: 12, kind: 1, flip: true },
    { left: 86, heightVh: 14, kind: 2, flip: false },
    { left: 92, heightVh: 13, kind: 3, flip: true },
    { left: 98, heightVh: 15, kind: 4, flip: false },
  ];

  // Middle tree band — medium height
  const middle = [
    { left: 3, heightVh: 24, kind: 2, flip: false },
    { left: 11, heightVh: 28, kind: 4, flip: true },
    { left: 19, heightVh: 22, kind: 0, flip: false },
    { left: 27, heightVh: 30, kind: 3, flip: true },
    { left: 35, heightVh: 25, kind: 1, flip: false },
    { left: 43, heightVh: 28, kind: 5, flip: true },
    { left: 51, heightVh: 23, kind: 2, flip: false },
    { left: 59, heightVh: 29, kind: 4, flip: true },
    { left: 67, heightVh: 25, kind: 0, flip: false },
    { left: 75, heightVh: 27, kind: 3, flip: true },
    { left: 83, heightVh: 23, kind: 1, flip: false },
    { left: 91, heightVh: 28, kind: 5, flip: true },
    { left: 99, heightVh: 24, kind: 2, flip: false },
  ];

  // Perspective corridor — tiny trees receding toward vanishing point at center
  // Filling in what used to be the light-beam gap
  const corridor = [
    { left: 40, heightVh: 9, kind: 2, flip: false },
    { left: 44, heightVh: 7, kind: 4, flip: true },
    { left: 47, heightVh: 6, kind: 0, flip: false },
    { left: 50, heightVh: 5.5, kind: 3, flip: true },
    { left: 53, heightVh: 6, kind: 5, flip: false },
    { left: 56, heightVh: 7, kind: 1, flip: true },
    { left: 60, heightVh: 9, kind: 2, flip: false },
  ];

  // Foreground — tall hero trees, peaking ~45vh for dramatic looming feel
  const front = [
    { left: 2, heightVh: 34, kind: 0, flip: false },
    { left: 8, heightVh: 48, kind: 3, flip: true },
    { left: 14, heightVh: 28, kind: 5, flip: false },
    { left: 20, heightVh: 42, kind: 2, flip: true },
    { left: 26, heightVh: 32, kind: 4, flip: false },
    { left: 32, heightVh: 36, kind: 1, flip: true },
    // — perspective: smaller trees receding to center vanishing point —
    { left: 38, heightVh: 22, kind: 4, flip: false },
    { left: 43, heightVh: 16, kind: 2, flip: true },
    { left: 48, heightVh: 13, kind: 5, flip: false },
    { left: 52, heightVh: 13, kind: 0, flip: true },
    { left: 57, heightVh: 16, kind: 3, flip: false },
    { left: 62, heightVh: 22, kind: 1, flip: true },
    // right flank
    { left: 68, heightVh: 30, kind: 3, flip: false },
    { left: 74, heightVh: 44, kind: 0, flip: true },
    { left: 80, heightVh: 28, kind: 5, flip: false },
    { left: 86, heightVh: 46, kind: 2, flip: true },
    { left: 92, heightVh: 34, kind: 4, flip: false },
    { left: 98, heightVh: 40, kind: 1, flip: true },
  ];

  // HERO foreground — massive pure-black trees closest to camera
  // Smaller trees now fill the middle to sustain the perspective corridor
  const hero = [
    { left: 3, heightVh: 62, kind: 0, flip: false },
    { left: 11, heightVh: 82, kind: 3, flip: true },
    { left: 22, heightVh: 55, kind: 4, flip: false },
    { left: 34, heightVh: 70, kind: 2, flip: true },
    // mid perspective — smaller foreground trees receding to center
    { left: 42, heightVh: 34, kind: 5, flip: false },
    { left: 50, heightVh: 26, kind: 1, flip: true },
    { left: 58, heightVh: 34, kind: 4, flip: false },
    // right flank
    { left: 66, heightVh: 72, kind: 1, flip: false },
    { left: 78, heightVh: 58, kind: 3, flip: true },
    { left: 89, heightVh: 84, kind: 0, flip: false },
    { left: 97, heightVh: 60, kind: 4, flip: true },
  ];

  const [pano1, pano2] = PANORAMAS;

  return (
    <>
      {/* Far panorama — full-width tree line, hazy, far back */}
      <div
        aria-hidden
        className="absolute left-0 right-0 pointer-events-none select-none"
        style={{ bottom: "-2vh", zIndex: 5, opacity: 0.5, filter: "blur(2.5px)" }}
      >
        <Panorama
          src={pano1[0]}
          naturalW={pano1[1]}
          naturalH={pano1[2]}
          heightVh={20}
          color="#080c1c"
        />
      </div>

      {/* Distant individual trees */}
      <ForestLayer
        trees={distant}
        z={6}
        blur={1.6}
        bottomOffsetVh={-2}
        opacity={0.65}
        color="#070b1c"
      />

      {/* Mid panorama — tree-line silhouette across whole bottom */}
      <div
        aria-hidden
        className="absolute left-0 right-0 pointer-events-none select-none"
        style={{ bottom: "-3vh", zIndex: 7, opacity: 0.85, filter: "blur(0.6px)" }}
      >
        <Panorama
          src={pano2[0]}
          naturalW={pano2[1]}
          naturalH={pano2[2]}
          heightVh={26}
          color="#04081a"
        />
      </div>

      {/* Middle individual trees */}
      <ForestLayer
        trees={middle}
        z={8}
        blur={0.4}
        bottomOffsetVh={-3}
        opacity={0.95}
        color="#04081a"
      />

      {/* Foreground hero trees — dense, tall, deep blue-black */}
      <ForestLayer
        trees={front}
        z={9}
        blur={0}
        bottomOffsetVh={-4}
        opacity={1}
        color="#02050f"
      />

      {/* HERO foreground — massive pure black trees, closest to camera */}
      <ForestLayer
        trees={hero}
        z={10}
        blur={0}
        bottomOffsetVh={-5}
        opacity={1}
        color="#000000"
      />

      {/* Perspective corridor — tiny distant trees at the vanishing point */}
      <ForestLayer
        trees={corridor}
        z={6}
        blur={1.2}
        bottomOffsetVh={-1}
        opacity={0.75}
        color="#060a1a"
      />
    </>
  );
};

export default function Hero() {
  const moonCenterY = NAV_TOP + PILL_HEIGHT / 2;
  const openInvite = useInvite();
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <section
      className="relative min-h-screen w-full overflow-hidden bg-black"
      data-testid="hero-section"
    >
      <SafeFx><StarField moonX={0.5} moonY={moonCenterY} moonRadius={HALO_RADIUS} /></SafeFx>

      {/* Northern lights / aurora — animated SVG ribbons */}
      <SafeFx><Aurora /></SafeFx>

      <Forest />

      <Link
        to="/program"
        className="group absolute top-0 left-0 right-0 z-30 flex items-center justify-center gap-3 px-4 text-center"
        style={{ height: BAR_H, background: "linear-gradient(180deg, rgba(2,4,14,0.85), rgba(2,4,14,0.55))", borderBottom: "1px solid rgba(233,214,162,0.14)", backdropFilter: "blur(10px)" }}
        data-testid="announcement-bar"
      >
        <span className="hidden sm:inline text-[10.5px] tracking-[0.18em] uppercase" style={{ color: "#c8bd9a" }}>
          Founder and Angel Readiness applications are now open.
        </span>
        <span className="sm:hidden text-[9.5px] tracking-[0.14em] uppercase whitespace-nowrap" style={{ color: "#c8bd9a" }}>
          Applications open
        </span>
        <span className="inline-flex items-center gap-1 text-[10.5px] tracking-[0.18em] uppercase whitespace-nowrap transition-colors group-hover:text-white" style={{ color: "#e9d6a2" }}>
          See the programs <ArrowUpRight size={12} />
        </span>
      </Link>

      {/* Desktop nav — twin liquid-glass pills */}
      <nav
        className="hidden xl:flex absolute left-0 right-0 z-20 items-center justify-center pointer-events-none"
        style={{ top: NAV_TOP }}
        data-testid="hero-nav"
      >
        <Pill links={leftLinks} side="left" />
        <MoonDisc />
        <Pill links={rightLinks} side="right" />
      </nav>

      {/* Mobile nav — compact bar */}
      <nav
        className="xl:hidden absolute left-0 right-0 z-20 flex items-center justify-between px-5"
        style={{ top: BAR_H + 18 }}
        data-testid="hero-nav-mobile"
      >
        <span aria-hidden className="w-9" />
        <MoonDisc />
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          className="p-2 rounded-full transition hover:bg-white/[0.05] pointer-events-auto"
          style={{ color: "#e9d6a2" }}
          data-testid="mobile-menu-open"
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
      </nav>

      {menuOpen && <MobileMenu onClose={() => setMenuOpen(false)} />}

      {/* Hero — quiet invitation card, speakeasy style */}
      <div
        className="absolute inset-x-0 z-20 flex flex-col items-center text-center px-6"
        style={{ top: "18%" }}
        data-testid="hero-content"
      >
        {/* Centered logo mark */}
        <div
          aria-hidden
          className="flex items-center gap-5 mb-10 opacity-95"
        >
          <span style={{ height: 1, width: 44, background: "linear-gradient(90deg, transparent, #bfa874)" }} />
          <img
            src="/brand/maven-m.webp"
            alt=""
            width={46}
            height={60}
            style={{ display: "block", filter: "drop-shadow(0 3px 18px rgba(140,180,235,0.42))" }}
          />
          <span style={{ height: 1, width: 44, background: "linear-gradient(90deg, #bfa874, transparent)" }} />
        </div>

        {/* Wordmark — real crystal via SVG specular lighting */}
        <div
          className="relative w-full max-w-[900px] mx-auto"
          data-testid="hero-wordmark-wrap"
          style={{
            filter:
              "drop-shadow(0 0 26px rgba(245,220,170,0.32)) drop-shadow(0 0 70px rgba(255,240,200,0.18))",
          }}
        >
          <h1 className="sr-only" data-testid="hero-wordmark">Maven Skies</h1>
          <svg
            aria-hidden
            viewBox="0 0 900 190"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: "100%", height: "auto", display: "block", overflow: "visible" }}
          >
            <defs>
              {/* Gemstone body — deep gold citrine gradient */}
              <linearGradient id="gemBody" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#fff5d4" />
                <stop offset="0.18" stopColor="#f6e2a8" />
                <stop offset="0.45" stopColor="#d9be74" />
                <stop offset="0.72" stopColor="#a88a4a" />
                <stop offset="1" stopColor="#6b5324" />
              </linearGradient>

              {/* Top rim highlight — bright hairline of light */}
              <linearGradient id="gemRim" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0"    stopColor="#ffffff" stopOpacity="0.95" />
                <stop offset="0.18" stopColor="#ffffff" stopOpacity="0.35" />
                <stop offset="0.35" stopColor="#ffffff" stopOpacity="0" />
              </linearGradient>

              {/* Bottom rim glow — reflected warm light */}
              <linearGradient id="gemBottom" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0.55" stopColor="#f4d98a" stopOpacity="0" />
                <stop offset="1"    stopColor="#f4d98a" stopOpacity="0.55" />
              </linearGradient>

              {/* Faint chromatic rainbow band — refractive dispersion */}
              <linearGradient id="gemPrism" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0.0"  stopColor="#a4d8ff" stopOpacity="0" />
                <stop offset="0.35" stopColor="#a4d8ff" stopOpacity="0.28" />
                <stop offset="0.55" stopColor="#ffffff" stopOpacity="0.30" />
                <stop offset="0.75" stopColor="#ffb6d5" stopOpacity="0.28" />
                <stop offset="1.0"  stopColor="#ffb6d5" stopOpacity="0" />
              </linearGradient>

              {/* Real 3D specular lighting — moving point light produces the
                  travelling crystal glint you see on cut gemstones */}
              <filter id="crystalLight" x="-10%" y="-40%" width="120%" height="180%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="1.6" result="blur" />
                <feSpecularLighting
                  in="blur"
                  surfaceScale="9"
                  specularConstant="2.2"
                  specularExponent="28"
                  lightingColor="#fffbe6"
                  result="spec"
                >
                  <fePointLight x="150" y="35" z="130">
                    <animate attributeName="x" values="80;820;80" dur="9s" repeatCount="indefinite" />
                    <animate attributeName="y" values="20;70;25" dur="9s" repeatCount="indefinite" />
                  </fePointLight>
                </feSpecularLighting>
                <feComposite in="spec" in2="SourceAlpha" operator="in" result="specClip" />
                <feMerge>
                  <feMergeNode in="SourceGraphic" />
                  <feMergeNode in="specClip" />
                </feMerge>
              </filter>

              {/* Sparkle pin-lights — a second faster micro-highlight */}
              <filter id="crystalSparkle" x="-10%" y="-40%" width="120%" height="180%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="0.8" result="b2" />
                <feSpecularLighting
                  in="b2"
                  surfaceScale="4"
                  specularConstant="3.0"
                  specularExponent="80"
                  lightingColor="#ffffff"
                  result="sp2"
                >
                  <fePointLight x="600" y="20" z="90">
                    <animate attributeName="x" values="820;80;820" dur="6s" repeatCount="indefinite" />
                    <animate attributeName="y" values="60;15;60" dur="6s" repeatCount="indefinite" />
                  </fePointLight>
                </feSpecularLighting>
                <feComposite in="sp2" in2="SourceAlpha" operator="in" />
              </filter>
            </defs>

            {/* Common text attrs kept consistent across layers */}
            {/* Base gemstone body with moving specular */}
            <text
              x="450" y="132" textAnchor="middle"
              fontFamily="Fraunces, serif" fontSize="128" fontWeight="600"
              letterSpacing="-3"
              fill="url(#gemBody)"
              filter="url(#crystalLight)"
            >
              Maven Skies
            </text>

            {/* Top hairline rim highlight */}
            <text
              x="450" y="132" textAnchor="middle"
              fontFamily="Fraunces, serif" fontSize="128" fontWeight="600"
              letterSpacing="-3"
              fill="url(#gemRim)"
              style={{ mixBlendMode: "screen" }}
            >
              Maven Skies
            </text>

            {/* Bottom warm reflection */}
            <text
              x="450" y="132" textAnchor="middle"
              fontFamily="Fraunces, serif" fontSize="128" fontWeight="600"
              letterSpacing="-3"
              fill="url(#gemBottom)"
              style={{ mixBlendMode: "screen" }}
            >
              Maven Skies
            </text>

            {/* Faint prismatic rainbow band drifting across */}
            <text
              x="450" y="132" textAnchor="middle"
              fontFamily="Fraunces, serif" fontSize="128" fontWeight="600"
              letterSpacing="-3"
              fill="url(#gemPrism)"
              style={{ mixBlendMode: "screen" }}
            >
              Maven Skies
            </text>

            {/* Pinpoint sparkle layer */}
            <text
              x="450" y="132" textAnchor="middle"
              fontFamily="Fraunces, serif" fontSize="128" fontWeight="600"
              letterSpacing="-3"
              fill="#ffffff"
              filter="url(#crystalSparkle)"
              style={{ mixBlendMode: "screen", opacity: 0.9 }}
            >
              Maven Skies
            </text>
          </svg>
        </div>

        {/* Single-line whisper */}
        <p
          className="mt-6 text-[13px] md:text-[14px] tracking-[0.28em] uppercase"
          style={{ color: "#c8bd9a", textShadow: "0 1px 12px rgba(0,0,0,0.7)" }}
          data-testid="hero-tagline"
        >
          A private Prairie Network
        </p>

        {/* Small quiet CTA */}
        <div className="mt-10 pointer-events-auto">
          <button
            type="button"
            onClick={openInvite}
            data-testid="hero-cta-request-invitation"
            className="group/btn relative inline-flex items-center gap-2 px-7 py-3 rounded-full text-[10.5px] tracking-[0.32em] uppercase font-semibold overflow-hidden transition-transform duration-300 hover:translate-y-[-1px]"
            style={{
              color: "#1a1408",
              background:
                "linear-gradient(135deg, #f7e9b8 0%, #d4b86a 25%, #f0dba0 50%, #b89752 75%, #e9d199 100%)",
              boxShadow:
                "inset 0 0 20px rgba(255,240,195,0.35), inset 0 1px 0 rgba(255,255,255,0.42), inset 0 -1px 0 rgba(120,90,40,0.35)",
            }}
          >
            {/* Internal star-like glow — soft radial pulse from within */}
            <span
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse 55% 100% at 50% 50%, rgba(255,246,210,0.55) 0%, rgba(255,236,190,0.20) 40%, rgba(255,236,190,0) 78%)",
                animation: "msStarPulse 3.6s ease-in-out infinite",
                mixBlendMode: "screen",
              }}
            />
            <span className="relative" style={{ zIndex: 2 }}>
              Request Invitation
            </span>
          </button>
        </div>
      </div>

    </section>
  );
}
